import { useField, useForm } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mixed, object as yupObject, string as yupString } from "yup";
import { TEXTFIELD_MAX_LENGTH_DEFAULT } from "@/shared/helper/constants.helper";
import type { InitialRegistrationData } from "@/app/registration-app/user/shared/@types/InitialRegistrationData";
import type { PersonalRegistrationData } from "@/app/registration-app/user/shared/@types/PersonalRegistrationData";
import { RegistrationSalutation } from "@/app/registration-app/user/shared/@types/RegistrationSalutation";

export const usePersonalInformationFields = (
  initialData?: InitialRegistrationData,
) => {
  const { t } = useI18n();
  const validationSchema = computed(() => {
    return yupObject({
      salutation: mixed<RegistrationSalutation>().required(
        t("shared.form.validation.required"),
      ),
      firstName: yupString()
        .trim()
        .required(t("shared.form.validation.required"))
        .max(
          TEXTFIELD_MAX_LENGTH_DEFAULT,
          t("shared.form.validation.max", {
            count: TEXTFIELD_MAX_LENGTH_DEFAULT,
          }),
        ),
      lastName: yupString()
        .trim()
        .required(t("shared.form.validation.required"))
        .max(
          TEXTFIELD_MAX_LENGTH_DEFAULT,
          t("shared.form.validation.max", {
            count: TEXTFIELD_MAX_LENGTH_DEFAULT,
          }),
        ),
      captcha: yupString().required(t("shared.form.validation.captcha")),
    });
  });

  const { handleSubmit, resetForm } = useForm<PersonalRegistrationData>({
    validationSchema,
  });

  const salutationOptions: {
    key: RegistrationSalutation;
    value: string;
  }[] = [
    {
      key: RegistrationSalutation.Mr,
      value: t("shared.address.salutations.mr"),
    },
    {
      key: RegistrationSalutation.Ms,
      value: t("shared.address.salutations.mrs"),
    },
    {
      key: RegistrationSalutation.Mx,
      value: t("shared.address.salutations.non_binary"),
    },
  ];

  const salutation = useField<RegistrationSalutation>("salutation", undefined, {
    initialValue: initialData?.salutation,
  });
  const firstName = useField<string>("firstName", undefined, {
    initialValue: initialData?.firstName,
  });
  const lastName = useField<string>("lastName", undefined, {
    initialValue: initialData?.lastName,
  });
  const phoneNumber = useField<string>("phoneNumber", undefined, {
    initialValue: initialData?.phone,
  });
  const captcha = useField<string>("captcha", undefined);

  return {
    handleSubmit,
    resetForm,
    salutation,
    salutationOptions,
    firstName,
    lastName,
    phoneNumber,
    captcha,
  };
};
